import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { LikeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { canCheerMatch, todayMatchState } from '../../shared/utils/feed-live';
import { feedRequest } from '../../lib/feedApi';
import { trackConversion } from '../../lib/conversionEvents';

const CHEERED_KEY = 'tenis.feed.cheered.';
const readCheered = (viewerId: string): string[] => {
  try {
    const value = JSON.parse(window.localStorage.getItem(`${CHEERED_KEY}${viewerId}`) || '[]');
    return Array.isArray(value) ? value.filter(item => typeof item === 'string').slice(-50) : [];
  } catch {
    return [];
  }
};
const saveCheered = (viewerId: string, ids: string[]) => {
  try {
    window.localStorage.setItem(`${CHEERED_KEY}${viewerId}`, JSON.stringify(ids.slice(-50)));
  } catch {
    // Depolama kapalı: sunucu tekrar isteği yine tek kayıt sayar.
  }
};

const LOCALES: Record<string, string> = { tr: 'tr-TR', en: 'en-GB', ru: 'ru-RU' };
const TICK_MS = 60 * 1000;

/**
 * "Bugün kortta": ligde bugün oynanacak/oynanan tekler maçları, saat sırasıyla yatay şerit.
 * Akışı zenginleştiren ek bilgidir; maç yoksa ya da okunamazsa hiç görünmez (akışın kendi
 * yükleniyor/boş/hata durumları değişmez). Durum etiketi dakikada bir yerelde güncellenir.
 */
export default function TodayMatches({
  leagueId,
  viewer,
}: {
  leagueId?: string | null;
  // B1: "İyi şanslar" için izleyici (aynı ligin üyesi olmalı).
  viewer?: Pick<Users.User, 'id' | 'lig_temel_id'> | null;
}) {
  const { t, i18n } = useTranslation('leagues');
  const locale = LOCALES[(i18n.language || 'tr').slice(0, 2)] || LOCALES.tr;
  const [state, setState] = useState<{ leagueId: string; matches: LeagueFeed.TodayMatch[] } | null>(
    null,
  );
  const [now, setNow] = useState(() => Date.now());
  const [cheered, setCheered] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setCheered(viewer?.id ? readCheered(viewer.id) : []);
  }, [viewer?.id]);

  useEffect(() => {
    if (!leagueId) return undefined;
    const controller = new AbortController();
    const load = () =>
      fetch(`/api/league-today?leagueId=${encodeURIComponent(leagueId)}`, {
        signal: controller.signal,
      })
        .then(response => (response.ok ? response.json() : Promise.reject(response.status)))
        .then(body => {
          setState({ leagueId, matches: Array.isArray(body?.matches) ? body.matches : [] });
          setNow(Date.now());
        })
        .catch(() => undefined);
    load();
    const onVisible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => {
      controller.abort();
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [leagueId]);

  const matches = state && state.leagueId === leagueId ? state.matches : [];
  const cheer = async (match: LeagueFeed.TodayMatch) => {
    if (!viewer?.id || busy) return;
    setBusy(match.id);
    setFailed(false);
    try {
      const result = await feedRequest<{ count: number }>(
        `/api/league-match-cheer?matchId=${encodeURIComponent(match.id)}`,
        'POST',
      );
      const next = Array.from(new Set([...cheered, match.id]));
      setCheered(next);
      saveCheered(viewer.id, next);
      setCounts(current => ({ ...current, [match.id]: result.count }));
      trackConversion('feed_match_cheer');
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };
  const names = (match: LeagueFeed.TodayMatch) =>
    match.players.map(player => `${player.ad} ${player.soyad}`.trim() || '—').join(' – ');
  if (!leagueId || !matches.length) return null;
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Istanbul',
  });

  // B1: başlamamış maça "İyi şanslar 👏"; verdiyse ya da veremiyorsa yalnız sayı.
  const cheerControl = (match: LeagueFeed.TodayMatch) => {
    const count = counts[match.id] ?? match.destek ?? 0;
    const mine = cheered.includes(match.id);
    const allowed =
      !!viewer?.id &&
      !mine &&
      canCheerMatch(
        {
          mac_durum: match.status,
          mac_saati: match.at,
          lig_temel_id: leagueId,
          kul_t_id: match.players[0]?.id,
          kul_r_id: match.players[1]?.id,
        },
        { id: viewer.id, lig_temel_id: viewer.lig_temel_id },
        now,
      );
    if (allowed) {
      return (
        <Button
          type="text"
          size="small"
          className="today-matches__cheer"
          loading={busy === match.id}
          onClick={() => cheer(match)}
          aria-label={t('detail.today.cheer', { players: names(match) })}
        >
          <LikeOutlined aria-hidden="true" /> {t('detail.today.cheerShort')}
          {count > 0 ? ` ${count}` : ''}
        </Button>
      );
    }
    if (!count) return null;
    return (
      <span
        className={`today-matches__cheers${mine ? ' is-mine' : ''}`}
        title={t('detail.today.cheerCount', { count })}
      >
        <LikeOutlined aria-hidden="true" /> {count}
        <span className="visually-hidden">{t('detail.today.cheerCount', { count })}</span>
      </span>
    );
  };

  return (
    <section className="today-matches" aria-labelledby={`today-${leagueId}`}>
      <h3 id={`today-${leagueId}`} className="today-matches__title">
        {t('detail.today.title')}
      </h3>
      {/* Yatay kayan şerit klavyeyle de kaydırılabilsin diye odaklanabilir. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <ul className="today-matches__list" tabIndex={0} aria-labelledby={`today-${leagueId}`}>
        {matches.map(match => {
          const status = todayMatchState(match.at, match.status, now);
          return (
            <li key={match.id} className={`today-matches__item is-${status}`}>
              <span className="today-matches__head">
                <span className="today-matches__state">
                  {status === 'live' && <span className="today-matches__dot" aria-hidden="true" />}
                  {status === 'upcoming' ? time.format(match.at) : t(`detail.today.${status}`)}
                </span>
                {cheerControl(match)}
              </span>
              {/* Tek satır, uzunsa üç nokta; tam adlar ipucunda. */}
              <span className="today-matches__players" title={names(match)}>
                {names(match)}
              </span>
              <span className="today-matches__meta">
                {status === 'upcoming'
                  ? match.court || t('detail.today.courtUnknown')
                  : `${time.format(match.at)}${match.court ? ` · ${match.court}` : ''}`}
              </span>
            </li>
          );
        })}
      </ul>
      {failed && (
        <p className="today-matches__error" role="alert">
          {t('detail.today.cheerError')}
        </p>
      )}
    </section>
  );
}

TodayMatches.defaultProps = { leagueId: null, viewer: null };
